import { useMemo, useState } from 'react';
import { Copy, Download } from 'lucide-react';
import { toast } from 'sonner';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { DateRangePicker, type DateRange } from '@/components/ui/date-range-picker';
import { copyToClipboard } from '@/lib/clipboard';
import { downloadJson } from '@/lib/download';
import {
  MAX_DIAS_EXPORT,
  buildProgramacaoExport,
  exportFileName,
  type ExportableEvent,
} from '@/lib/export-programacao';
import { todayISO } from '@/lib/dates';

const SECTION_MONO = 'font-mono text-[12px] uppercase tracking-wider text-muted-foreground';

interface Props {
  open: boolean;
  onClose: () => void;
  events: ExportableEvent[];
}

function defaultRange(): DateRange {
  const hoje = todayISO();
  const d = new Date();
  d.setDate(d.getDate() + MAX_DIAS_EXPORT - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return { inicio: hoje, fim: `${y}-${m}-${dd}` };
}

export function ExportProgramacaoSheet({ open, onClose, events }: Props) {
  const [range, setRange] = useState<DateRange | null>(defaultRange);

  const result = useMemo(
    () => (range ? buildProgramacaoExport(events, range) : null),
    [events, range],
  );

  const totalEventos = result?.data.dias.length ?? 0;
  // O plugin recusa arquivo sem nenhum dia.
  const podeExportar = totalEventos > 0;

  function avisarExtras() {
    if (!result) return;
    const { diasComVariosEventos, diasSemDj } = result;
    if (diasComVariosEventos.length > 0) {
      toast.warning(
        `${diasComVariosEventos.length} ${diasComVariosEventos.length === 1 ? 'dia tinha' : 'dias tinham'} mais de um evento — só o primeiro entrou no arquivo.`,
      );
    }
    if (diasSemDj.length > 0) {
      toast.warning(
        `${diasSemDj.length} ${diasSemDj.length === 1 ? 'dia ficou' : 'dias ficaram'} de fora por não ter DJ no line-up: ${diasSemDj.join(', ')}.`,
      );
    }
  }

  function handleDownload() {
    if (!result || !range || !podeExportar) return;
    downloadJson(exportFileName(range), result.data);
    toast.success(`Programação exportada · ${totalEventos} evento(s)`);
    avisarExtras();
    onClose();
  }

  async function handleCopy() {
    if (!result || !podeExportar) return;
    const ok = await copyToClipboard(`${JSON.stringify(result.data, null, 2)}\n`);
    if (!ok) {
      toast.error('Não foi possível copiar');
      return;
    }
    toast.success('JSON copiado');
    avisarExtras();
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Exportar programação"
      titleClassName="font-mono text-[12px] font-semibold uppercase tracking-wider text-foreground"
      maxWidth="max-w-3xl"
    >
      <p className={`${SECTION_MONO} -mt-1 mb-3`}>Escolha o intervalo de datas</p>

      <DateRangePicker
        value={range}
        onChange={setRange}
        onApply={handleDownload}
        onCancel={onClose}
        applyLabel="Baixar JSON"
        applyDisabled={!podeExportar}
        maxDays={MAX_DIAS_EXPORT}
      />

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className={SECTION_MONO}>
          {podeExportar
            ? `${totalEventos} ${totalEventos === 1 ? 'dia' : 'dias'} no arquivo`
            : 'Nenhum evento publicado no período'}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleCopy()}
            disabled={!podeExportar}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-foreground transition active:scale-[0.97] hover:bg-muted disabled:opacity-50"
          >
            <Copy className="h-3.5 w-3.5" /> Copiar JSON
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!podeExportar}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 font-mono text-[11px] font-semibold uppercase tracking-wider text-primary-foreground shadow-red transition active:scale-[0.97] disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" /> Baixar JSON
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
